import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { acessoSchema, type AcessoDef, type OpcoesConcessao, type UsuarioLinha } from '@triade/contracts';
import { useAdicionarAcesso, useDesativarUsuario, useReativarUsuario, useRemoverAcesso } from '@/api/usuarios';
import { RecordToolbar, SaidaComAlteracoesDialog, useBlockerDirty } from '@/components/record-toolbar';
import { Button } from '@/components/ui/button';
import { ApiError } from '@/lib/api';
import { EditorAcessos } from './editor-acessos';
import { SituacaoUsuarioBadge } from './situacao-badge';
import { diffAcessos, ordenarAcessos, separarAcessos, situacaoUsuario } from './tipos';

const formSchema = z.object({ acessos: z.array(acessoSchema).max(60) });
type FormValores = z.infer<typeof formSchema>;

function mensagemErro(e: unknown): string {
  return e instanceof ApiError ? `${e.message}${e.code ? ` (${e.code})` : ''}` : 'falha de comunicação com a API';
}

export interface UsuarioDetalheProps {
  usuario: UsuarioLinha;
  opcoes: OpcoesConcessao;
  /** Posicao na grade (anterior/proximo da barra). */
  index: number;
  total: number;
  onAnterior?: () => void;
  onProximo?: () => void;
  onLista: () => void;
  onConvidar: () => void;
}

/**
 * Formulario do usuario: acessos por empresa (varios papeis). Salvar grava so a diferenca (adicionar/remover acesso) e a API
 * confere o teto de cada um. A barra de registro oferece anterior/proximo, desfazer, novo (convidar) e desativar.
 */
export function UsuarioDetalhe({ usuario, opcoes, index, total, onAnterior, onProximo, onLista, onConvidar }: UsuarioDetalheProps) {
  const { editaveis, travados } = useMemo(() => separarAcessos(usuario.acessos), [usuario.acessos]);
  const form = useForm<FormValores>({ resolver: zodResolver(formSchema), defaultValues: { acessos: editaveis } });
  const { isDirty } = form.formState;
  const acessos = form.watch('acessos');
  const adicionar = useAdicionarAcesso();
  const remover = useRemoverAcesso();
  const desativar = useDesativarUsuario();
  const reativar = useReativarUsuario();
  const [salvando, setSalvando] = useState(false);
  const [salvoEm, setSalvoEm] = useState<Date | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const bloqueio = useBlockerDirty(isDirty);

  // O servidor mudou (salvou, outra aba): o formulario volta a refletir o gravado, se nao houver edicao em curso.
  useEffect(() => {
    if (!form.formState.isDirty) form.reset({ acessos: editaveis });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editaveis]);

  const { adicionar: aAdicionar, remover: aRemover } = useMemo(() => diffAcessos(usuario.acessos, acessos), [usuario.acessos, acessos]);
  const alterados = aAdicionar.length + aRemover.length;

  async function salvar() {
    setErro(null);
    setSalvando(true);
    try {
      // remover antes de adicionar: trocar de papel na mesma empresa nunca passa por um estado com os dois
      for (const a of aRemover) await remover.mutateAsync({ usuarioId: usuario.id, acessoId: a.id });
      for (const a of aAdicionar as AcessoDef[]) await adicionar.mutateAsync({ usuarioId: usuario.id, acesso: a });
      setSalvoEm(new Date());
      form.reset({ acessos: ordenarAcessos(acessos) });
    } catch (e) {
      setErro(mensagemErro(e));
    } finally {
      setSalvando(false);
    }
  }

  async function acao(fn: () => Promise<unknown>) {
    setErro(null);
    try {
      await fn();
    } catch (e) {
      setErro(mensagemErro(e));
    }
  }

  const inativo = !usuario.ativo;

  return (
    <section aria-labelledby="titulo-usuario" data-testid="detalhe-usuario" className="flex flex-col gap-4 rounded-2xl border border-border bg-card p-5">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 id="titulo-usuario" className="text-title font-semibold">
            {usuario.nome}
          </h2>
          <p className="text-small text-ink-secondary">{usuario.email}</p>
        </div>
        <div className="flex items-center gap-2">
          <SituacaoUsuarioBadge situacao={situacaoUsuario(usuario)} />
          {inativo && usuario.podeReativar && (
            <Button type="button" variant="outline" size="sm" onClick={() => void acao(() => reativar.mutateAsync(usuario.id))} disabled={reativar.isPending}>
              Reativar usuário
            </Button>
          )}
        </div>
      </header>

      {inativo && (
        <p role="note" className="rounded-xl border border-border bg-line-soft px-4 py-3 text-small text-ink-body">
          Usuário desativado: não entra no sistema. Os acessos ficam guardados e voltam ao reativar.
        </p>
      )}
      {usuario.acessosForaDoEscopo > 0 && (
        <p role="note" className="rounded-xl border border-border bg-line-soft px-4 py-3 text-small text-ink-body">
          Este usuário tem acessos em outras empresas, fora do seu alcance. Eles não aparecem aqui e ele só pode ser desativado por quem alcança todos.
        </p>
      )}

      <form onSubmit={(e) => { e.preventDefault(); void salvar(); }} noValidate aria-label="Acessos do usuário">
        <h3 className="mb-2 text-small font-semibold">Acessos por empresa</h3>
        <EditorAcessos
          idPrefixo="u"
          opcoes={opcoes}
          valor={acessos}
          travados={travados}
          desabilitado={salvando}
          onChange={(v) => form.setValue('acessos', ordenarAcessos(v), { shouldDirty: true })}
        />
      </form>

      {erro && (
        <p role="alert" className="rounded-md bg-destructive/10 px-3 py-2 text-small text-danger-ink">
          {erro}
        </p>
      )}

      <RecordToolbar
        dirty={isDirty}
        saving={salvando}
        savedAt={salvoEm}
        erro={erro}
        index={index}
        total={total}
        canPrev={!!onAnterior}
        canNext={!!onProximo}
        alteradosCount={isDirty ? alterados : undefined}
        onList={onLista}
        onPrev={onAnterior}
        onNext={onProximo}
        onNew={onConvidar}
        onSave={() => void salvar()}
        onUndo={() => {
          form.reset({ acessos: editaveis });
          setErro(null);
        }}
        onDelete={usuario.podeDesativar ? () => void acao(() => desativar.mutateAsync(usuario.id)) : () => undefined}
        excluirTitulo={usuario.podeDesativar ? `Desativar ${usuario.nome}?` : `Você não pode desativar ${usuario.nome}`}
        excluirDescricao={
          usuario.podeDesativar
            ? 'O usuário deixa de entrar no sistema agora (as sessões abertas caem) e os acessos ficam guardados. Você pode reativá-lo depois.'
            : usuario.ehVoce
              ? 'Você não pode desativar o próprio usuário.'
              : !usuario.ativo
                ? 'Este usuário já está desativado.'
                : 'Este usuário tem acessos acima do seu papel ou fora do seu escopo. Remova só os acessos da sua empresa.'
        }
        excluirConfirmar={usuario.podeDesativar ? 'Desativar' : 'Entendi'}
        excluirRotulo="Desativar usuário"
      />
      <SaidaComAlteracoesDialog {...bloqueio} />
    </section>
  );
}
